import { useCallback, useEffect, useState } from 'react';
import { PrinterSettings } from '../components/PrinterSettings';
import type { DriverRow, PickupPointRow, ZoneRow } from '../lib/types';
import { PickupPointForm, ZoneForm } from '../components/PickupPointForm';
import { DeviceList } from '../components/DeviceList';
import { TenantSettings } from '../components/TenantSettings';
import { ZonePricingImport } from '../components/ZonePricingImport';
import { ZoneKeywords } from '../components/ZoneKeywords';
import { ZoneBoundaryEditor } from '../components/ZoneBoundaryEditor';
import { api } from '../lib/api';
import { ChannelSettings } from '../components/ChannelSettings';
import { FeatureToggles } from '../components/FeatureToggles';
import { DriverPolicies } from '../components/DriverPolicies';
import { GeocodeSettings, SmsSettings } from '../components/GeocodeSettings';
import { CustomerSettings } from '../components/CustomerSettings';
import { DictSettings } from '../components/DictSettings';
import { StorageSettings } from '../components/StorageSettings';
import { MapSettings } from '../components/MapSettings';
import { ThresholdSettings } from '../components/ThresholdSettings';
import { UserSettings } from '../components/UserSettings';
import { RoleSettings } from '../components/RoleSettings';
import { ChangePassword } from './ChangePassword';
import { useRouteSegment } from '../lib/route';
import { CircleParking, CornerDownRight, Factory, MessageCircle, Store } from 'lucide-react';
import { InlineIcon } from '../components/InlineIcon';

export function Settings({
  zones,
  points,
  drivers,
  permissions,
  onChanged,
}: {
  zones: ZoneRow[];
  points: PickupPointRow[];
  drivers: DriverRow[];
  permissions: string[];
  onChanged: () => Promise<void>;
}) {
  const [editPoint, setEditPoint] = useState<PickupPointRow | 'new' | null>(null);
  const [editZone, setEditZone] = useState<ZoneRow | 'new' | null>(null);
  const [keywordZone, setKeywordZone] = useState<ZoneRow | null>(null);
  /** 正在画范围的区;哪些区已经画过(列表上标出来) */
  const [boundaryZone, setBoundaryZone] = useState<ZoneRow | null>(null);
  const [drawn, setDrawn] = useState<Set<string>>(new Set());
  const loadDrawn = useCallback(() => {
    api<Array<{ id: string }>>('/zones/boundaries')
      .then((rows) => setDrawn(new Set(rows.map((r) => r.id))))
      .catch(() => setDrawn(new Set()));
  }, []);
  useEffect(() => {
    loadDrawn();
  }, [loadDrawn]);

  const close = async () => {
    setEditPoint(null);
    setEditZone(null);
    await onChanged();
  };

  /*
   * ★ 设置页原来是**一条长滚动**:取货点、区域、商家各有几十张卡片,
   *   而功能开关、司机策略、地理编码排在它们后面 —— 用户反馈说
   *   「一直没看到下面还有系统配置」。
   *
   *   这不是排序问题:再怎么排,总有东西被挤到看不见的地方。
   *   改成左边一栏目录、右边只显示一块 —— 每一块都是一次点击可达,
   *   而且**打开设置页时看得到一共有哪些东西**。
   */
  const TABS = [
    // 租户信息排第一 —— 时区和地区决定其余所有设置怎么解释
    { key: 'tenant', label: '租户信息', show: true },
    { key: 'points', label: '取货点', show: true },
    { key: 'zones', label: '配送区域', show: true },
    { key: 'customers', label: '商家', show: permissions.includes('customer:read') },
    { key: 'features', label: '功能开关', show: true },
    // 字典:特殊物品这类「自己加几项」的下拉,加在功能开关旁边 ——
    // 它们都属于「这套系统在我们这儿长什么样」
    { key: 'dict', label: '字典', show: true },
    { key: 'drivers', label: '司机策略', show: true },
    { key: 'thresholds', label: '运营阈值', show: true },
    { key: 'geocode', label: '地理编码', show: true },
    { key: 'map', label: '地图底图', show: true },
    // 打印机紧挨着地图/存储这些「设备和外部东西」的设置
    { key: 'printers', label: '打印机', show: permissions.includes('setting:manage') },
    { key: 'storage', label: '对象存储', show: true },
    { key: 'sms', label: '短信通知', show: true },
    { key: 'channels', label: '三方通道', show: permissions.includes('integration:read') },
    /*
     * ★ 接口密钥挨着「三方通道」:两者都是「和外面的系统打交道」,
     *   只是方向相反 —— 通道是我们往外推,密钥是别人往里下单。
     */
    /*
     * ★ 用户和角色排在一起,而且都用 `user:read` 看门 ——
     *   「有哪些人、他们是什么角色」是同一个问题的两半,
     *   分给两个权限的话总有一半看不到,那一屏就成了半张地图。
     */
    { key: 'users', label: '用户', show: permissions.includes('user:read') },
    { key: 'roles', label: '角色', show: permissions.includes('user:read') },
    // 改自己的密码 —— 谁都该有,不需要任何权限
    { key: 'password', label: '修改密码', show: true },
    // 谁都看得到自己的设备 —— 这一屏管的是「我的账号」,不是租户配置
    { key: 'devices', label: '登录设备', show: true },
  ].filter((t) => t.show);
  /*
   * ★ 设置里的二级标签也写进地址栏(`#/settings/storage`)——
   *   设置这一屏尤其需要:改完配置刷新看生效没有,是最常见的动作,
   *   而每次都被扔回「取货点」。
   */
  const [tab, setTab] = useRouteSegment(1, 'points');

  return (
    /*
     * ★ 手机上目录横过来。
     *   竖着的 160px 目录在 390px 宽的屏上又吃掉四成 ——
     *   而设置页里的表单本来就窄不了。
     *   横向滚动的一条在这里是合适的:十来个短词,划一下就到头,
     *   而且**一眼能看出还有更多**(和九列表格横滚不是一回事)。
     */
    <div className="flex min-h-dvh flex-col lg:flex-row">
      <nav
        aria-label="设置分类"
        className="flex shrink-0 gap-1 overflow-x-auto border-b border-slate-200 p-2
                   lg:w-40 lg:flex-col lg:border-b-0 lg:border-r lg:p-3 dark:border-slate-700"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`mb-0 shrink-0 rounded-lg px-3 py-2 text-left text-sm transition
              lg:mb-1 lg:w-full ${
              tab === t.key
                ? 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-400'
                : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700/50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="min-w-0 flex-1 space-y-8 p-4 lg:p-6">
      {tab === 'printers' && <PrinterSettings />}

      {tab === 'points' && (
      <section>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-xl font-semibold lg:text-2xl text-slate-900 dark:text-slate-100">取货点</h1>
          <button
            onClick={() => setEditPoint('new')}
            className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium"
          >
            + 新建取货点
          </button>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {points.map((p) => (
            <button
              key={p.id}
              onClick={() => setEditPoint(p)}
              className="text-left rounded-xl border border-slate-200 dark:border-slate-700
                         bg-white dark:bg-slate-800 p-4 hover:border-brand-400 transition"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium text-slate-900 dark:text-slate-100">
                    <InlineIcon icon={p.type === 'WAREHOUSE' ? Factory : Store} /> {p.name}
                    <span className="ml-2 text-xs font-mono text-slate-400">{p.code}</span>
                  </div>
                  <div className="text-sm text-slate-500 mt-1">{p.address}</div>
                </div>
                {(p.pendingOrders ?? 0) > 0 && (
                  <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-blue-100
                                   dark:bg-blue-950 text-blue-700 dark:text-blue-300">
                    待取 {p.pendingOrders}
                  </span>
                )}
              </div>

              {/* 新司机第一次去最需要的两条 */}
              {p.pickupInstruction && (
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                  <InlineIcon icon={MessageCircle} /> {p.pickupInstruction}
                </p>
              )}
              {p.parkingNote && (
                <p className="mt-1 text-sm text-slate-500"><InlineIcon icon={CircleParking} /> {p.parkingNote}</p>
              )}

              <div className="mt-2 flex gap-3 text-xs text-slate-400">
                {p.cutoffTime && <span>截单 {p.cutoffTime}</span>}
                <span>围栏 {p.geofenceRadiusM}m</span>
                {p.lat == null && <span className="text-amber-500">⚠ 缺少坐标</span>}
              </div>
            </button>
          ))}
        </div>
      </section>
      )}

      {tab === 'users' && <UserSettings canManage={permissions.includes('user:manage')} />}
      {tab === 'roles' && <RoleSettings canManage={permissions.includes('role:manage')} />}
      {tab === 'password' && (
        <section aria-label="修改密码">
          <h2 className="mb-1 text-xl font-semibold text-slate-900 dark:text-slate-100">
            修改密码
          </h2>
          <p className="mb-4 text-sm text-slate-500">
            改完之后其它设备上的登录会全部失效 —— 包括你手机上那个
          </p>
          <div className="max-w-sm">
            <ChangePassword embedded onDone={onChanged} onCancel={() => setTab('tenant')} />
          </div>
        </section>
      )}

      {tab === 'zones' && (
      <section>
        <ZonePricingImport onApplied={onChanged} />
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">配送区域</h2>
          <button
            onClick={() => setEditZone('new')}
            className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium"
          >
            + 新建区域
          </button>
        </div>
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto
                        bg-white dark:bg-slate-800">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-600 dark:text-slate-400">
              <tr>
                <th className="p-3 text-left font-medium">编码</th>
                <th className="p-3 text-left font-medium">名称</th>
                <th className="p-3 text-left font-medium">优先级</th>
                <th className="p-3 text-left font-medium">偏远</th>
                <th className="p-3 text-left font-medium">默认司机</th>
                <th className="p-3 text-left font-medium">活跃订单</th>
                <th className="p-3 text-left font-medium">关键词</th>
                <th className="p-3 text-left font-medium">范围</th>
              </tr>
            </thead>
            <tbody>
              {zones.map((z) => (
                <tr
                  key={z.id}
                  onClick={() => setEditZone(z)}
                  className="border-t border-slate-100 dark:border-slate-700/50 cursor-pointer
                             hover:bg-slate-50 dark:hover:bg-slate-700/30"
                >
                  <td className="p-3 font-mono text-xs text-slate-500">{z.code}</td>
                  <td className="p-3 text-slate-900 dark:text-slate-100">
                    {z.name}
                    {/*
                      ★ 把上级写在名字底下。
                        子区域自己不配价的时候,钱是按上级那条规则算的 ——
                        运营在这张表上看不到这层关系的话,
                        「这一片怎么是这个价」永远查不出来。
                    */}
                    {z.parent && (
                      <span className="ml-2 text-xs text-slate-400"><InlineIcon icon={CornerDownRight} /> 计价随「{z.parent.name}」</span>
                    )}
                  </td>
                  <td className="p-3 tabular-nums text-slate-600 dark:text-slate-400">{z.priority}</td>
                  <td className="p-3">
                    {z.isRemote ? (
                      <span className="text-xs px-2 py-0.5 rounded bg-orange-100 dark:bg-orange-950
                                       text-orange-700 dark:text-orange-400">
                        偏远 +{(Number(z.remoteSurcharge) / 100).toFixed(2)}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="p-3 text-slate-600 dark:text-slate-400">
                    {z.defaultDriver?.name ?? '—'}
                  </td>
                  <td className="p-3 tabular-nums text-slate-600 dark:text-slate-400">
                    {z.activeOrders}
                  </td>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setKeywordZone(z)}
                      className="text-xs text-brand-600 dark:text-brand-500 hover:underline"
                    >
                      配置关键词
                    </button>
                  </td>
                  {/*
                    ★ 在地图上圈出这个区。订单坐标落在圈里就以圈为准,不看地址里写了什么词 ——
                      「地址里带着偏远区的词、其实离得很近」靠这个分清。
                  */}
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setBoundaryZone(z)}
                      className="text-xs text-brand-600 dark:text-brand-500 hover:underline"
                    >
                      {drawn.has(z.id) ? '已画 · 改范围' : '画范围'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {/* 商家决定了取货时拍不拍照 */}
      {tab === 'customers' && permissions.includes('customer:read') && (
        <CustomerSettings
          canManage={permissions.includes('customer:manage')}
          canManageApi={permissions.includes('setting:manage')}
          points={points}
        />
      )}

      {tab === 'dict' && <DictSettings canManage={permissions.includes('setting:manage')} />}

      {tab === 'storage' && <StorageSettings canManage={permissions.includes('setting:manage')} />}

      {tab === 'geocode' && <GeocodeSettings canManage={permissions.includes('setting:manage')} />}

      {tab === 'map' && <MapSettings canManage={permissions.includes('setting:manage')} />}

      {tab === 'sms' && <SmsSettings canManage={permissions.includes('setting:manage')} />}

      {tab === 'tenant' && (
        <TenantSettings canManage={permissions.includes('setting:manage')} />
      )}

      {tab === 'devices' && <DeviceList />}

      {tab === 'thresholds' && (
        <ThresholdSettings canManage={permissions.includes('setting:manage')} />
      )}

      {tab === 'drivers' && (
        <DriverPolicies canManage={permissions.includes('driver:policy_manage')} />
      )}

      {tab === 'features' && <FeatureToggles canManage={permissions.includes('setting:manage')} />}

      {/* 看得到通道配置的人比能改的人多 —— 实施要看,运维要看 */}
      {tab === 'channels' && permissions.includes('integration:read') && (
        <ChannelSettings canManage={permissions.includes('integration:manage')} />
      )}

      </div>

      {editPoint && (
        <PickupPointForm
          existing={editPoint === 'new' ? undefined : editPoint}
          zones={zones}
          onClose={() => setEditPoint(null)}
          onSaved={close}
        />
      )}

      {boundaryZone && (
        <ZoneBoundaryEditor
          zone={boundaryZone}
          onClose={() => setBoundaryZone(null)}
          onSaved={loadDrawn}
        />
      )}

      {keywordZone && (
        <ZoneKeywords
          zoneId={keywordZone.id}
          zoneName={`${keywordZone.code} ${keywordZone.name}`}
          onClose={() => setKeywordZone(null)}
        />
      )}

      {editZone && (
        <ZoneForm
          existing={editZone === 'new' ? undefined : editZone}
          drivers={drivers.map((d) => ({ id: d.id, name: d.name }))}
          zones={zones}
          onClose={() => setEditZone(null)}
          onSaved={close}
        />
      )}
    </div>
  );
}
